import axios from 'axios';
import authHeader from './auth-header';

// API base URL from environment variables
// Update the API_URL definition to ensure it's correct
const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000/api/';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // "Access-Control-Allow-Origin": "*",
  },
  timeout: 30000, // 10 seconds timeout
  withCredentials: true, // ✅ CRITICAL: Enable session support
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth headers to every request
    const headers = authHeader();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        config.headers[key] = headers[key];
      });
    }

    // Log outgoing requests in development
    if (process.env.NODE_ENV === 'development') {
      // console.log('API Request:', config.method.toUpperCase(), config.url, config.data || {});
    }

    // ✅ CRITICAL: Add session handling headers
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // console.log(`📡 API Request: ${config.method.toUpperCase()} ${config.url}`, {
    //   data: config.data,
    //   params: config.params,
    //   headers: config.headers,
    // });

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Standardize successful responses
    if (process.env.NODE_ENV === 'development') {
      // console.log('API Response:', response.status, response.data);
    }
    return response;
  },
  (error) => {
    // Handle errors gracefully
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.status, error.response.data);

      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        // Could dispatch logout action or redirect to login
        // store.dispatch('auth/logout');
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('API No Response:', error.request);
    } else {
      // Something went wrong in setting up the request
      console.error('API Config Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// API Service class
export default {
  // Generic HTTP methods
  // Update the get method to handle debugging better
  async get(url, params = {}) {
    // Add debugging
    // console.log(`Making GET request to: ${this.getBaseUrl()}${url}`, params);

    // Return the modified promise with better error handling
    try {
      return await apiClient.get(url, { params });
    } catch (error) {
      // Use debugUtils for more detailed logging
      if (window.debugUtils) {
        debugUtils.debugError('API Request Failed', {
          url: `${this.getBaseUrl()}${url}`,
          params,
          errorType: error.request
            ? 'No Response'
            : error.response
            ? 'Error Response'
            : 'Request Setup Error',
          status: error.response?.status,
          message: error.message,
          responseData: error.response?.data,
        });
      }

      throw error;
    }
  },
  post(url, data = {}) {
    return apiClient.post(url, data);
  },

  put(url, data = {}) {
    return apiClient.put(url, data);
  },

  patch(url, data = {}) {
    return apiClient.patch(url, data);
  },

  delete(url) {
    return apiClient.delete(url);
  },

  // Specific API endpoints
  listings: {
    getAll(params = {}) {
      return apiClient.get('listings', { params });
    },

    getById(id) {
      return apiClient.get(`listings/${id}`);
    },

    filter(filters = {}) {
      return apiClient.post('filter-listings', filters);
    },
  },

  categories: {
    getAll() {
      return apiClient.get('categories-public');
    },

    getById(id) {
      return apiClient.get(`categories/${id}`);
    },
  },
  getBaseUrl() {
    return API_URL;
  },

  // Update the amenities section with more detailed logging

  amenities: {
    async getAll() {
      // console.log('🌐 API SERVICE: Calling amenities.getAll()');
      // console.log('🌐 Full API URL:', `${API_URL}amenities`);
      // console.log('🌐 Base URL check:', API_URL);

      try {
        const response = await apiClient.get('amenities');
        // console.log('✅ API SERVICE: amenities.getAll() success:', {
        //   status: response.status,
        //   statusText: response.statusText,
        //   headers: response.headers,
        //   dataType: typeof response.data,
        //   dataKeys: response.data ? Object.keys(response.data) : 'No data keys',
        //   dataLength: Array.isArray(response.data)
        //     ? response.data.length
        //     : response.data.data && Array.isArray(response.data.data)
        //     ? response.data.data.length
        //     : 'unknown',
        //   firstAmenity: response.data.data
        //     ? response.data.data[0]
        //     : Array.isArray(response.data)
        //     ? response.data[0]
        //     : 'No first amenity',
        //   responseStructure: JSON.stringify(response.data, null, 2).substring(0, 500),
        // });
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: amenities.getAll() error:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          fullUrl: `${API_URL}amenities`,
          headers: error.response?.headers,
        });

        // Check if it's a network error
        if (!error.response) {
          console.error('🌐 Network Error - Could not reach server:', {
            baseURL: API_URL,
            timeout: apiClient.defaults.timeout,
            requestConfig: error.config,
          });
        }

        throw error;
      }
    },

    async getById(id) {
      // console.log('🌐 API SERVICE: Calling amenities.getById() for ID:', id);
      try {
        const response = await apiClient.get(`amenities/${id}`);
        // console.log('✅ API SERVICE: amenities.getById() success for ID:', id, response.data);
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: amenities.getById() error for ID:', id, error);
        throw error;
      }
    },
  },

  // ===== RECENTLY VIEWED API METHODS =====
  recentlyViewed: {
    /**
     * Mark a listing as viewed
     */
    async markAsViewed(listingId, metadata = {}) {
      // console.log('👁️ API SERVICE: Marking listing as viewed:', listingId);
      try {
        const response = await apiClient.post(`listing-views/mark/${listingId}`, metadata);
        // console.log('✅ API SERVICE: markAsViewed success:', response.data);
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: markAsViewed error:', error);
        throw error;
      }
    },

    /**
     * Get recently viewed listings
     */
    async getRecentlyViewed(params = {}) {
      const { limit = 20, hours = 168 } = params;
      // console.log('📋 API SERVICE: Getting recently viewed listings:', {
      //   limit,
      //   hours,
      // });

      try {
        const response = await apiClient.get('listing-views', {
          params: { limit, hours },
        });
        // console.log('✅ API SERVICE: getRecentlyViewed success:', {
        //   count: response.data.data?.length || 0,
        //   meta: response.data.meta,
        // });
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: getRecentlyViewed error:', error);
        throw error;
      }
    },

    /**
     * Get similar listings based on recently viewed
     */
    async getSimilarListings(params = {}) {
      const { limit = 10, hours = 168 } = params;
      // Getting similar listings

      try {
        const response = await apiClient.get('listing-views/similar', {
          params: { limit, hours },
        });
        // getSimilarListings success
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: getSimilarListings error:', error);
        throw error;
      }
    },

    /**
     * Clear all recently viewed
     */
    async clearAll() {
      try {
        const response = await apiClient.delete('listing-views/clear');
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: clearAll error:', error);
        throw error;
      }
    },

    /**
     * Get view statistics
     */
    async getStats() {
      try {
        const response = await apiClient.get('listing-views/stats');
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: getStats error:', error);
        throw error;
      }
    },
  },

  // ✅ FIXED: Use consistent apiClient instance
  savedSearches: {
    /**
     * Get all saved searches for the current user
     */
    async getSavedSearches() {
      try {
        const response = await apiClient.get('searches', {
          params: { pc_format: true },
        }); // ✅ FIXED: Use apiClient
        //const response = await apiClient.get('pc-searches'); // ✅ FIXED: Use apiClient

        return response;
      } catch (error) {
        console.error('❌ API SERVICE: getSavedSearches failed:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
        });
        throw error;
      }
    },

    /**
     * Save a new search
     */

    async saveSearch(searchData) {
      return await apiClient.post('searches', {
        ...searchData,
        saved_listings: JSON.stringify(searchData.savedListings),
        search_metadata: JSON.stringify(searchData.searchMetadata),
        notify: searchData.notifyEnabled,
      });
    },

    /**
     * Delete a saved search
     */
    async deleteSearch(id) {
      try {
        const response = await apiClient.delete(`searches/${id}`); // ✅ FIXED: Use apiClient
        return response;
      } catch (error) {
        console.error('❌ API SERVICE: deleteSearch failed:', error);
        throw error;
      }
    },
  },
  // User listings
  userListings: {
    getListings(userId) {
      return apiClient.get(`user-listings/${userId}`);
    },

    deleteListing(id) {
      return apiClient.delete(`listings/${id}`);
    },
  },
};
